import merge from 'deepmerge';
import { createReducer } from '@reduxjs/toolkit';
import _get from 'get-value';
import DuckSelector from './DuckSelector.js';

function isFunction(func) {
    return func !== null && typeof func === 'function';
}

function isUndefined(value) {
    return typeof value === 'undefined' || value === undefined;
}

function assignDefaults(options) {
    return {
        ...options,
        consts: options.consts || {},
        creators: options.creators || (() => ({})),
        selectors: options.selectors || {},
        types: options.types || [],
    };
}

function injectDuck(input, duck) {
    if (input instanceof Function) {
        return input(duck);
    }
    return input;
}

/**
 * Helper utility to assist in composing the selectors.
 * Previously defined selectors can be used to derive future selectors.
 *
 * @param {object} selectors
 * @returns
 */
function deriveSelectors(selectors) {
    const composedSelectors = {};

    Object.keys(selectors).forEach((key) => {
        const selector = selectors[key];

        switch (selector.constructor) {
            case DuckSelector:
                composedSelectors[key] =
                    selector.injectSelectors(composedSelectors);
                break;

            default:
                composedSelectors[key] = selector;
                break;
        }
    });

    return composedSelectors;
}

class Duck {
    constructor(options) {
        this.options = assignDefaults(options);

        const {
            namespace,
            store,
            storePrefix,
            types,
            consts,
            initialState,
            creators,
            selectors,
            useRTKReducer,
            primitiveState,
        } = this.options;

        // Assign supplied constants to the Duck
        Object.entries(consts).forEach(([key, value = []]) => {
            this[key] = value.reduce(
                (acc, cv) => ({
                    ...acc,
                    [cv]: cv,
                }),
                {}
            );
        });

        this.store = [storePrefix, store].filter(Boolean).join('');
        this.path = this.store;
        this.primitiveState = primitiveState;

        // generate duck specific type constants
        this.types = types.reduce(
            (acc, type) => ({
                ...acc,
                [type]: [namespace, this.path.replace(/\./g, '/'), type]
                    .filter(Boolean)
                    .join('/'),
            }),
            {}
        );

        this.initialState = isFunction(initialState)
            ? initialState(this)
            : initialState;

        if (useRTKReducer) {
            this.reducer = createReducer(this.initialState, (builder) =>
                this.options.reducer(builder, this)
            );
        } else {
            this.reducer = this.reducer.bind(this);
        }

        this.selectors = deriveSelectors({
            getDuckState: (state) =>
                _get(state, this.path, { default: this.initialState }),
            ...injectDuck(selectors, this),
        });

        this.creators = creators(this);
    }

    reducer(state, action) {
        if (isUndefined(state)) {
            state = this.initialState;
        }

        return this.options.reducer(state, action, this);
    }

    extend(extensionOptions) {
        if (isFunction(extensionOptions)) {
            extensionOptions = extensionOptions(this);
        }

        extensionOptions = assignDefaults(extensionOptions);

        const parentOptions = this.options;

        let initialState;
        let extendedReducer;

        if (isFunction(extensionOptions.initialState)) {
            initialState = (duck) =>
                extensionOptions.initialState(duck, this.initialState);
        } else if (isUndefined(extensionOptions.initialState)) {
            initialState = parentOptions.initialState;
        } else {
            initialState = extensionOptions.initialState;
        }

        if (parentOptions.useRTKReducer) {
            extendedReducer = (builder, duck) => {
                parentOptions.reducer(builder, duck);
                extensionOptions.reducer(builder, duck);
            };
        } else {
            extendedReducer = (state, action, duck) => {
                state = parentOptions.reducer(state, action, duck);

                if (isUndefined(extensionOptions.reducer)) {
                    return state;
                }
                return extensionOptions.reducer(state, action, duck);
            };
        }

        return new Duck({
            ...parentOptions,
            ...extensionOptions,
            initialState,
            consts: merge(parentOptions.consts, extensionOptions.consts),
            creators: (duck) => {
                const parentCreators = parentOptions.creators(duck);
                return {
                    ...parentCreators,
                    ...extensionOptions.creators(duck, parentCreators),
                };
            },
            reducer: extendedReducer,
            selectors: (duck) => ({
                ...injectDuck(parentOptions.selectors, duck),
                ...injectDuck(extensionOptions.selectors, duck),
            }),
            types: [...parentOptions.types, ...extensionOptions.types],
        });
    }
}

// add legacy reference to DuckSelector onto the Duck
Duck.Selector = DuckSelector;

export { Duck as default };
